// src/repository/cube/CubeClient.ts
//
// Deviation from BaseDAO.ts / AnalyticsDAO.ts: those wrap a generated OpenAPI `Api` class
// (backed by swagger.json) via useApiStore's apiConfig. Cube.js is a separate semantic-layer
// service that is not part of that OpenAPI spec, so this file talks to it directly with
// plain `fetch`.
//
// Transport (plans/2026-09-14-navigator-insights-production-transport-plan.md, M2):
// browser -> `/api/cube` (a Static Web Apps managed function, see `api/src/functions/cube.js`)
// -> Cube. The function verifies this request's Auth0 bearer token, mints the Cube JWT
// server-side, and forwards to Cube's `/cubejs-api/v1/load` - the Cube credential never
// reaches the browser bundle. In local dev, the Vite proxy re-points this same path at the
// sandbox Cube instance (see vite.config.js) so there is only one URL in every environment.

/** Re-pointed in one edit if the Cube transport ever moves (e.g. behind a BFF). */
export const CUBE_API_BASE_PATH = '/api/cube';

// The function at CUBE_API_BASE_PATH owns the `/cubejs-api/v1/load` suffix now - appending
// it here would double it up and 404 silently against the deployed function's route.
const CUBE_LOAD_ENDPOINT = CUBE_API_BASE_PATH;

export type CubeFilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'set'
  | 'notSet'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte';

export type CubeFilter = {
  member: string;
  operator: CubeFilterOperator;
  values: string[];
};

export type CubeTimeDimension = {
  dimension: string;
  dateRange: [string, string];
  /** Omit entirely for whole-window (ungrouped) measures - see Rule 2. */
  granularity?: 'day';
};

export type CubeQuery = {
  measures?: string[];
  dimensions?: string[];
  filters?: CubeFilter[];
  timeDimensions?: CubeTimeDimension[];
  order?: Record<string, 'asc' | 'desc'>;
  /** A dimensions-only, row-level query (e.g. `getWorkZoneBreaches`) is unbounded by
   * construction - callers that need truncation visibility should request one row beyond
   * their intended cap and surface that in their own result shape, rather than trusting
   * Cube's default page size implicitly. */
  limit?: number;
};

/** One row of a Cube `/load` response. Values arrive as JSON strings for numeric
 * measures (confirmed against real Cube responses in M0) - see toNumber/toRatio. */
export type CubeRow = Record<string, unknown>;

type CubeLoadResponse = {
  error?: string;
  data?: CubeRow[];
};

/** Thrown when `loadCube` is called before an Auth0 token is available, instead of
 * silently sending an unauthenticated request that the function would just 403. */
export class CubeAuthTokenMissingError extends Error {
  constructor() {
    super('No Auth0 access token available for the Cube request.');
    this.name = 'CubeAuthTokenMissingError';
  }
}

/**
 * POSTs a query to Cube's `/load` endpoint (via the `/api/cube` function) and returns
 * the row data.
 *
 * Rule 4: Cube replies with a `{"error": "..."}` body on HTTP 200 for query errors
 * (e.g. an unknown member). `.error` must be checked before `.data` is ever trusted,
 * regardless of `response.ok`.
 */
export async function loadCube(query: CubeQuery): Promise<CubeRow[]> {
  // Dynamic (not static) import, and non-hook access, matching the DAO pattern (e.g.
  // OrganizationDAO.ts's `useApiStore.getState().apiConfig`) - this file is a plain
  // module, not a component. Dynamic so that merely importing `CubeClient`/`InsightsDAO`
  // (several presentational Insights components import a constant from InsightsDAO,
  // which re-exports from this file) never eagerly evaluates apiStore.ts's module-level
  // `create()` call, which throws if `VITE_COMPASS_PRO_API` is unset for that test run.
  // Not `Ocp-Apim-Subscription-Key`: this route does not go through APIM.
  const { useApiStore } = await import('@/stores/apiStore');
  const { token } = useApiStore.getState();
  if (!token) {
    throw new CubeAuthTokenMissingError();
  }

  const response = await fetch(CUBE_LOAD_ENDPOINT, {
    method: 'POST',
    headers: {
      // Not `Authorization` (2026-09-15 finding): Azure Static Web Apps' managed-Functions
      // proxy unconditionally overwrites that header with its own internal service token
      // before `api/src/functions/cube.js` ever sees it - a known, still-open platform
      // limitation (https://github.com/Azure/static-web-apps/issues/34). Sending the real
      // Auth0 token under this custom header name lets it survive the proxy hop; the
      // function reads `x-cube-authorization` accordingly.
      'X-Cube-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query }),
  });

  const body = (await response.json()) as CubeLoadResponse;

  if (body.error) {
    throw new Error(body.error);
  }
  if (!response.ok) {
    throw new Error(`Cube request failed with HTTP ${response.status}`);
  }

  return body.data ?? [];
}

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

/**
 * Coerces a raw Cube measure value to a number. Cube's ClickHouse driver serializes
 * numeric aggregates as JSON strings (e.g. `"184"`, `"0.5"`) - confirmed against every
 * M0 fixture - so this must never rely on `typeof x === 'number'` (Rule 3). This is one
 * of the only two places numeric coercion happens anywhere in the Insights feature.
 */
export function toNumber(value: unknown): number | null {
  if (isBlank(value)) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Coerces a raw Cube ratio measure (e.g. `compliance_ratio`, `reporting_coverage_ratio`)
 * to a number. Ratios use the same string-encoded numeric shape as any other measure, so
 * this delegates to `toNumber` rather than duplicating the coercion logic; it exists as a
 * distinct export because callers should type ratio fields as ratios, not raw counts.
 */
export function toRatio(value: unknown): number | null {
  return toNumber(value);
}
